/**
 * CTRL_04 Dashboard - Explainability & Transparency
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress, Alert } from 'antd';
import { CheckCircleOutlined, WarningOutlined, BulbOutlined, PercentageOutlined } from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const METHOD_COLORS = {
  'SHAP': '#1890ff',
  'LIME': '#52c41a',
  'attention': '#722ed1',
  'gradient': '#fa8c16',
  'counterfactual': '#13c2c2'
};

function CTRL_04_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_04';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const explainabilityCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, explained: 0, percentage: 0 };
    const explained = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.explainability_method || metadata.explanation_available === true;
    }).length;
    return { total: events.length, explained, percentage: Math.round((explained / events.length) * 100) };
  }, [events]);

  const methodDistribution = useMemo(() => {
    const distribution = {};
    events.forEach(e => {
      const method = e.event_metadata?.explainability_method;
      if (method) {
        distribution[method] = (distribution[method] || 0) + 1;
      }
    });
    return Object.entries(distribution).map(([method, count]) => ({
      method,
      count,
      percentage: events.length > 0 ? Math.round((count / events.length) * 100) : 0,
      color: METHOD_COLORS[method] || '#888'
    }));
  }, [events]);

  const confidenceScores = useMemo(() => {
    const scores = events
      .map(e => e.event_metadata?.confidence_score)
      .filter(score => score !== undefined && score !== null);
    if (scores.length === 0) return { average: 0, min: 0, max: 0 };
    const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    return { average: average.toFixed(1), min: Math.min(...scores), max: Math.max(...scores) };
  }, [events]);

  const violations = useMemo(() => {
    return events.filter(e => {
      const metadata = e.event_metadata || {};
      return !metadata.explainability_method && metadata.explanation_available !== true;
    });
  }, [events]);

  const columns = [
    { title: 'Timestamp', dataIndex: 'ts', key: 'ts', render: (ts) => new Date(ts).toLocaleString(), width: 180 },
    { title: 'Model', dataIndex: 'model', key: 'model', ellipsis: true },
    {
      title: 'Method',
      key: 'method',
      render: (_, record) => {
        const method = record.event_metadata?.explainability_method;
        return method ?
          <Tag color={METHOD_COLORS[method] || '#888'}>{method}</Tag> :
          <Tag color="red">Missing</Tag>;
      }
    },
    {
      title: 'Confidence',
      dataIndex: ['event_metadata', 'confidence_score'],
      key: 'confidence',
      align: 'center',
      render: (score) => score !== undefined ? `${score}%` : '-'
    },
    {
      title: 'Available',
      key: 'available',
      align: 'center',
      render: (_, record) => {
        const available = record.event_metadata?.explanation_available === true;
        return available ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="orange">No</Tag>;
      }
    }
  ];

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  if (!projectId) {
    return <Empty description="Please select a project to view dashboard" style={{ padding: '40px' }} />;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_04: Explainability & Transparency Dashboard</h2>

      {violations.length > 0 && (
        <Alert
          message={`${violations.length} inference events missing explainability`}
          description="Some AI decisions lack explanations. Implement SHAP, LIME, or attention mechanisms."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
          icon={<WarningOutlined />}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Explainability Coverage"
              value={explainabilityCoverage.percentage}
              suffix="%"
              prefix={explainabilityCoverage.percentage >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: explainabilityCoverage.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {explainabilityCoverage.explained} of {explainabilityCoverage.total} explained
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Confidence"
              value={confidenceScores.average}
              suffix="%"
              prefix={<PercentageOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Methods Used"
              value={methodDistribution.length}
              prefix={<BulbOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Violations"
              value={violations.length}
              prefix={violations.length === 0 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: violations.length === 0 ? '#52c41a' : '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Explainability Method Distribution" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {methodDistribution.map((item, idx) => (
            <Col xs={24} sm={12} md={8} key={idx} style={{ marginBottom: '16px' }}>
              <Card size="small" style={{ backgroundColor: `${item.color}15`, borderColor: item.color }}>
                <Statistic
                  title={item.method}
                  value={item.count}
                  suffix={`(${item.percentage}%)`}
                  valueStyle={{ color: item.color }}
                />
                <Progress percent={item.percentage} strokeColor={item.color} size="small" style={{ marginTop: '8px' }} />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Card title="Recent Inferences">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} events` }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_04_Dashboard;
